import axios from '~/axiosWrapper';
import {action, Action, computed, Computed, thunk, Thunk} from 'easy-peasy';
import {CommonModel, getCommonModel, fetch, fetchCancel} from 'store/commonStore';
import {API} from 'app/routes';
import {StoreModel} from 'store/index';
import {DEFAULT_DAILY_INSPECTION_TARGET, DEFAULT_ENVIRONMENTAL_DASHBOARD_CALCULATION_MODE, EnvironmentalDashboardCalculationMode, Machine} from 'types/index';
import {MachineOfflineService} from "~/sqlite/services/machine";
import { MaxFavoriteAssets } from '~/app/data/statuses/machine';
import { DEFAULT_MAX_IMAGE_SIZE, DEFAULT_MAX_NUMBER_OF_IMAGES, DEFAULT_MAX_NUMBER_OF_PDFS, DEFAULT_MAX_PDF_SIZE } from '~/training/types';

interface MachinesFavoritePayload {
    machineId: Machine['id'],
    favorite: boolean
}

interface MachinesLastUsedPayload {
    machineId: Machine['id']
}

export interface TrainingAttachmentLimit {
    maxImageSize: number;
    maxNumberOfImages: number;
    maxPDFSize: number;
    maxNumberOfPDFs: number;
}

export interface MachinesModel extends CommonModel<Machine> {
    
    filteredList: Computed<MachinesModel, Machine[], StoreModel>

    lastUsed: Machine[]
    fetchingLastUsed: boolean
    setFetchingLastUsed: Action<MachinesModel, boolean>
    fetchLastUsed: Thunk<MachinesModel, void, any, StoreModel, Promise<Machine[]>>
    fetchLastUsedCancel: Action<MachinesModel>
    fetchedLastUsed: Action<MachinesModel, Machine[]>

    inFavorite: Computed<MachinesModel, (id: Machine['id']) => boolean, StoreModel>
    favorites: Machine[]
    fetchingFavorites: boolean
    setFetchingFavorites: Action<MachinesModel, boolean>
    fetchFavorites: Thunk<MachinesModel, void, any, StoreModel, Promise<Machine[]>>
    fetchFavoritesCancel: Action<MachinesModel>
    fetchedFavorites: Action<MachinesModel, Machine[]>

    favoritesUpdating: boolean
    setFavoritesUpdating: Action<MachinesModel, boolean>
    updateFavorites: Thunk<MachinesModel, MachinesFavoritePayload, any, StoreModel, Promise<null>>
    updatedFavorites: Action<MachinesModel, Machine[]>

    updateLastUsed: Thunk<MachinesModel, MachinesLastUsedPayload, any, StoreModel, Promise<null>>

    setOfflineFavorites: Thunk<MachinesModel, Machine[], any, StoreModel, Promise<void>>
    getOfflineFavorites: Thunk<MachinesModel, void, any, StoreModel, Promise<void>>

    favoriteAssetsLimit: number
    setFavoriteAssetsLimit: Action<MachinesModel, number>

    isFavoriteAssetsLimitReached: Computed<MachinesModel, boolean, StoreModel>

    periodicCheckReportIntervalType: string
    setPeriodicCheckReportIntervalType: Action<MachinesModel, string>

    periodicCheckReportIntervalCount: number
    setPeriodicCheckReportIntervalCount: Action<MachinesModel, number>

    periodicCheckReportAverageIntervalType1: string
    setPeriodicCheckReportAverageIntervalType1: Action<MachinesModel, string>

    periodicCheckReportAverageIntervalCount1: number
    setPeriodicCheckReportAverageIntervalCount1: Action<MachinesModel, number>

    periodicCheckReportAverageIntervalType2: string
    setPeriodicCheckReportAverageIntervalType2: Action<MachinesModel, string>

    periodicCheckReportAverageIntervalCount2: number
    setPeriodicCheckReportAverageIntervalCount2: Action<MachinesModel, number>

    minimumRequiredItems: number
    setminimumRequiredItems: Action<MachinesModel, number>

    trainingAttachmentLimit: TrainingAttachmentLimit
    setTrainingAttachmentLimit: Action<MachinesModel, TrainingAttachmentLimit>
    /** General setting: standard working hours per day (hours, e.g. 7.5). Set from permissions like minimumRequiredItems. */
    standardWorkingHoursPerDay: number
    setStandardWorkingHoursPerDay: Action<MachinesModel, number | string | undefined>

    dailyInspectionTarget: number
    setDailyInspectionTarget: Action<MachinesModel, number>

    environmentalDashboardCalculationMode: number
    setEnvironmentalDashboardCalculationMode: Action<MachinesModel, number>

    showPerformanceComparison: Computed<MachinesModel, boolean, StoreModel>

    allowedIssueFileExtensions: string[]
    setAllowedIssueFileExtensions: Action<MachinesModel, string[]>

    allowedTrainingFileExtensions: string[]
    setAllowedTrainingFileExtensions: Action<MachinesModel, string[]>

    allowedChatFileExtensions: string[]
    setAllowedChatFileExtensions: Action<MachinesModel, string[]>
}

export const machinesModel: MachinesModel = {
    ...getCommonModel<Machine>(API.machines),

    filteredList: computed(state => state.list.filter(item => (
        !state.lastUsed.find(({id}) => item.id === id) &&
        !state.favorites.find(({id}) => item.id === id)
    ))),

    lastUsed: [],
    fetchingLastUsed: false,
    setFetchingLastUsed: action((state, payload) => {
        state.fetchingLastUsed = payload;
    }),
    fetchLastUsed: thunk((actions) => {
        actions.setFetchingLastUsed(true);

        return fetch<Machine>(`${API.machines}/last-used`)
            .then(data => {
                actions.fetchedLastUsed(data);
                return data;
            })
            .finally(() => {
                actions.setFetchingLastUsed(false);
            });
    }),
    fetchLastUsedCancel: action(() => {
        fetchCancel('Operation canceled by the user');
    }),
    fetchedLastUsed: action((state, payload) => {
        state.lastUsed = payload;
    }),

    inFavorite: computed(
        state => (id: Machine['id']): boolean => !!state.favorites.find(item => item.id.toString() === id.toString())
    ),
    favorites: [],
    fetchingFavorites: true,
    setFetchingFavorites: action((state, payload) => {
        state.fetchingFavorites = payload;
    }),
    fetchFavorites: thunk((actions) => {
        actions.setFetchingFavorites(true);

        return fetch<Machine>(`${API.machines}/favorite`)
            .then(data => {
                actions.fetchedFavorites(data);
                actions.setOfflineFavorites(data);

                return data;
            })
            .finally(() => {
                actions.setFetchingFavorites(false);
            });
    }),
    fetchFavoritesCancel: action(() => {
        fetchCancel('Operation canceled by the user');
    }),
    fetchedFavorites: action((state, payload) => {
        state.favorites = payload;
    }),

    favoritesUpdating: false,
    setFavoritesUpdating: action((state, payload) => {
        state.favoritesUpdating = payload;
    }),
    updateFavorites: thunk((actions, payload) => {
        actions.setFavoritesUpdating(true);

        return axios.patch<null, null>(`${API.machines}/favorite`, payload).then(
            data => fetch<Machine>(`${API.machines}/favorite`)
                .then(list => {
                    actions.updatedFavorites(list);
                    actions.setOfflineFavorites(list);

                    return data;
                })
                .finally(() => {
                    actions.setFavoritesUpdating(false);
                })
        )
    }),
    updatedFavorites: action((state, payload) => {
        state.favorites = payload;
    }),

    updateLastUsed: thunk((actions, payload) => {
        return axios.post<null, null>(`${API.machines}/last-used`, payload);
    }),

    setOfflineFavorites: thunk(async (actions, payload) => {
        const MachineOffline = new MachineOfflineService();
        await MachineOffline.reset(payload);
    }),

    getOfflineFavorites: thunk(async (actions) => {
        const MachineOffline = new MachineOfflineService();
        let machines = await MachineOffline.getAll();
        machines && actions.updatedFavorites(
            machines.map(
                (machine) =>
                    ({
                        ...JSON.parse(machine.data),
                        id: machine.id,
                        categoryImage: `${window['cordova'].file.dataDirectory}/${machine.categoryImage}`
                    })
            )
        );
    }),

    favoriteAssetsLimit: MaxFavoriteAssets.LIMIT,
    
    setFavoriteAssetsLimit: action((state, payload) => {
        state.favoriteAssetsLimit = payload ?? MaxFavoriteAssets.LIMIT;
    }),

    isFavoriteAssetsLimitReached: computed(
        (state) => state.favorites.length >= (state.favoriteAssetsLimit || MaxFavoriteAssets.LIMIT)
    ),

    periodicCheckReportIntervalType: 'day',
    
    setPeriodicCheckReportIntervalType: action((state, payload) => {
        state.periodicCheckReportIntervalType = payload ?? 'day';
    }),

    periodicCheckReportIntervalCount: 7,
    
    setPeriodicCheckReportIntervalCount: action((state, payload) => {
        state.periodicCheckReportIntervalCount = payload ?? 7;
    }),

    periodicCheckReportAverageIntervalType1: 'month',
    
    setPeriodicCheckReportAverageIntervalType1: action((state, payload) => {
        state.periodicCheckReportAverageIntervalType1 = payload ?? 'month';
    }),

    periodicCheckReportAverageIntervalCount1: 6,
    
    setPeriodicCheckReportAverageIntervalCount1: action((state, payload) => {
        state.periodicCheckReportAverageIntervalCount1 = payload ?? 6;
    }),

    periodicCheckReportAverageIntervalType2: 'month',
    
    setPeriodicCheckReportAverageIntervalType2: action((state, payload) => {
        state.periodicCheckReportAverageIntervalType2 = payload ?? 'month';
    }),

    periodicCheckReportAverageIntervalCount2: 12,
    
    setPeriodicCheckReportAverageIntervalCount2: action((state, payload) => {
        state.periodicCheckReportAverageIntervalCount2 = payload ?? 12;
    }),

    minimumRequiredItems: 3,
    
    setminimumRequiredItems: action((state, payload) => {
        state.minimumRequiredItems = payload ?? 3;
    }),

    trainingAttachmentLimit: {
        maxImageSize: DEFAULT_MAX_IMAGE_SIZE,
        maxNumberOfImages: DEFAULT_MAX_NUMBER_OF_IMAGES,
        maxPDFSize: DEFAULT_MAX_PDF_SIZE,
        maxNumberOfPDFs: DEFAULT_MAX_NUMBER_OF_PDFS
    },
    
    setTrainingAttachmentLimit: action((state, payload) => {
        state.trainingAttachmentLimit = payload;
    }),

    standardWorkingHoursPerDay: 7.5,

    setStandardWorkingHoursPerDay: action((state, payload) => {
        let h: number;
        if (typeof payload === 'number' && payload > 0) {
            h = payload;
        } else if (typeof payload === 'string') {
            h = parseFloat(payload.replace(',', '.'));
        } else {
            h = 7.5;
        }
        state.standardWorkingHoursPerDay = h > 0 ? h : 7.5;
    }),

    dailyInspectionTarget: DEFAULT_DAILY_INSPECTION_TARGET,
    
    setDailyInspectionTarget: action((state, payload) => {
        state.dailyInspectionTarget = payload ?? DEFAULT_DAILY_INSPECTION_TARGET;
    }),

    environmentalDashboardCalculationMode: DEFAULT_ENVIRONMENTAL_DASHBOARD_CALCULATION_MODE,

    setEnvironmentalDashboardCalculationMode: action((state, payload) => {
        state.environmentalDashboardCalculationMode = payload ?? DEFAULT_ENVIRONMENTAL_DASHBOARD_CALCULATION_MODE;
    }),

    showPerformanceComparison: computed((state) => state.environmentalDashboardCalculationMode === EnvironmentalDashboardCalculationMode.CATEGORY_BENCHMARK),

    allowedIssueFileExtensions: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'],
    setAllowedIssueFileExtensions: action((state, payload) => {
        state.allowedIssueFileExtensions = payload ?? ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
    }),

    allowedTrainingFileExtensions: ['pdf'],
    setAllowedTrainingFileExtensions: action((state, payload) => {
        state.allowedTrainingFileExtensions = payload ?? ['pdf'];
    }),

    allowedChatFileExtensions: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'heic'],
    setAllowedChatFileExtensions: action((state, payload) => {
        state.allowedChatFileExtensions = payload ?? ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'heic'];
    }),
};
